import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Spinner } from '../../components/ui/spinner';
import { fmtDate, fmtDateTime } from '../../lib/format';
import { ArrowLeft, User, MapPin, Building, Phone } from 'lucide-react';

interface AssociadoDetalhe {
  id: string;
  documento: string;
  documentoMask: string;
  nome: string;
  dataNascimento: string | null;
  matriculaServidor: string;
  orgaoPublico: string | null;
  situacaoServidor: string | null;
  email: string | null;
  celular: string | null;
  cep: string | null;
  endereco: string | null;
  numero: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  banco: string | null;
  agencia: string | null;
  conta: string | null;
  tipoConta: string | null;
  pixChave: string | null;
  status: string;
  createdAt: string;
  agente: { id: string; nome: string } | null;
  contratos: Array<{ id: string; numeroContrato: string; etapaOperacional: string; statusFinanceiro: string }>;
}

const statusColors: Record<string, string> = {
  em_analise: 'bg-amber-100 text-amber-900',
  ativo: 'bg-green-100 text-green-900',
  inativo: 'bg-gray-100 text-gray-700',
  liquidado: 'bg-blue-100 text-blue-900',
  bloqueado: 'bg-red-100 text-red-900',
};

function Row({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono text-xs' : ''}>{value ?? '—'}</span>
    </div>
  );
}

export function AssociadoDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ['associado', id],
    queryFn: () => api<AssociadoDetalhe>(`/associados/${id}`),
    enabled: !!id,
  });

  if (isLoading) return <Spinner />;
  if (!data) return <p>Associado não encontrado</p>;

  return (
    <div className="space-y-6 max-w-4xl">
      <Link to="/associados" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" />voltar para associados
      </Link>

      <div>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-3xl font-semibold tracking-tight">{data.nome}</h1>
          <span className={`text-xs px-2 py-1 rounded ${statusColors[data.status] ?? 'bg-gray-100'}`}>
            {data.status.replace('_', ' ')}
          </span>
        </div>
        <p className="text-muted-foreground font-mono text-sm">
          {data.documentoMask} · matrícula {data.matriculaServidor}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><User className="h-4 w-4" />Pessoal</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <Row label="Data de nascimento" value={fmtDate(data.dataNascimento)} />
            <Row label="E-mail" value={data.email} />
            <Row label="Celular" value={data.celular} />
            <Row label="Agente" value={data.agente?.nome ?? '—'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Building className="h-4 w-4" />Vínculo público</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <Row label="Órgão" value={data.orgaoPublico} />
            <Row label="Situação" value={data.situacaoServidor} />
            <Row label="Matrícula" value={data.matriculaServidor} mono />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" />Endereço</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <Row label="CEP" value={data.cep} />
            <Row label="Endereço" value={[data.endereco, data.numero].filter(Boolean).join(', ') || null} />
            <Row label="Bairro" value={data.bairro} />
            <Row label="Cidade/UF" value={[data.cidade, data.uf].filter(Boolean).join(' / ') || null} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Phone className="h-4 w-4" />Bancário</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <Row label="Banco" value={data.banco} />
            <Row label="Agência / Conta" value={data.agencia ? `${data.agencia} / ${data.conta ?? ''}` : null} />
            <Row label="Tipo de conta" value={data.tipoConta} />
            <Row label="PIX" value={data.pixChave} mono />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contratos</CardTitle>
          <CardDescription>{data.contratos.length} contrato(s)</CardDescription>
          <div className="mt-2">
            <Link to={`/contratos/novo?associadoId=${data.id}`} className="text-sm text-primary hover:underline">+ novo contrato</Link>
          </div>
        </CardHeader>
        <CardContent>
          {data.contratos.length === 0 && (
            <p className="text-sm text-muted-foreground">Nenhum contrato vinculado ainda.</p>
          )}
          {data.contratos.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-2">Número</th>
                  <th className="text-left py-2">Etapa</th>
                  <th className="text-left py-2">Status financeiro</th>
                </tr>
              </thead>
              <tbody>
                {data.contratos.map((c) => (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="py-2 font-mono text-xs"><Link to={`/contratos/${c.id}`} className="text-primary hover:underline">{c.numeroContrato}</Link></td>
                    <td className="py-2">{c.etapaOperacional.replace('_', ' ')}</td>
                    <td className="py-2">{c.statusFinanceiro.replace('_', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">Cadastrado em {fmtDateTime(data.createdAt)}</p>
    </div>
  );
}
